import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icon, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { useTingEnrollment } from "pages/offer/ting/hooks";
import { TingContext, type TingContextState } from "pages/offer/ting/ting-flow";
import { formatTwoLineAddress } from "utils";

import styles from "./ting-offer.module.scss";
import { tingPolicyCardUiIcon } from "./ting-policy-helpers";

export const ContactInformationConfirmation = () => {
  const { data: selectedPolicy } = useSelectedPolicy();
  const navigate = useNavigate();
  const { state, formNavigate } =
    useMultiStepFormState<TingContextState>(TingContext);

  const { enrollTing, loading, error } = useTingEnrollment(state);
  const [submissionError, setSubmissionError] = useState<string[]>([]);
  const [submissionSuccess, setSubmissionSuccess] = useState<string[]>([]);

  const skipPropertyStep = state?.eligibleTingPolicies?.length === 1;
  const stepLabels = skipPropertyStep
    ? ["Confirm details", "Review and finish"]
    : ["Select properties", "Confirm details", "Review and finish"];

  const policiesToEnroll =
    state?.eligibleTingPolicies?.filter((p) =>
      (
        state?.selectedTingPolicyIds || [selectedPolicy?.keystonePolicyId]
      ).includes(p.keystonePolicyId),
    ) ?? [];

  useEffect(() => {
    if (
      !(
        state?.phone &&
        state?.firstName &&
        state?.lastName &&
        state?.emailAddress
      )
    ) {
      navigate("../policy-confirmation");
      return;
    }
  }, [
    navigate,
    policiesToEnroll.length,
    state?.emailAddress,
    state?.firstName,
    state?.lastName,
    state?.phone,
  ]);

  const onSubmit = async () => {
    for (const policy of policiesToEnroll) {
      const success = await enrollTing({
        eligibilityInfo: policy.eligibilityDetails,
        propertyAddress: policy.propertyAddress,
      });
      if (success) {
        setSubmissionSuccess((prev) => [...prev, policy.keystonePolicyId]);
      }
      if (error) {
        setSubmissionError((prev) => [...prev, policy.keystonePolicyId]);
      }
    }
  };

  if (submissionSuccess.length === policiesToEnroll.length) {
    navigate("/my/property-services/ting-offer/registered");
  }

  if (submissionError.length > 0) {
    throw new Error(
      "error signing up for Ting offer for policies: " +
        submissionError.join(", "),
    );
  }

  return (
    <MultiStepFormLayout
      showLogo
      labels={stepLabels}
      step={skipPropertyStep ? 2 : 3}
      totalSteps={skipPropertyStep ? 2 : 3}
      heading="Review details and finish enrollment"
      buttonProps={{
        primary: { text: "Finish", onPress: onSubmit, loading: loading },
        cancel: { path: "/my/property-services/ting-offer" },
        promptOnCancel: true,
        formNavigate,
      }}
    >
      <Stack>
        <div className={styles.iconItemSm}>
          <div className={styles.iconDS}>
            <Icon name="User" size="lg" />
          </div>
          <div>
            <Span bold>
              {state?.firstName} {state?.lastName}
            </Span>
            <div className={styles.subtext}>{state?.emailAddress}</div>
            <div className={styles.subtext}>{state?.phone}</div>
          </div>
        </div>
        {policiesToEnroll.map((policy) => {
          const { lineOne, lineTwo } = formatTwoLineAddress(
            policy.propertyAddress ?? {},
            true,
          );
          const propertyIcon = tingPolicyCardUiIcon(policy);
          return (
            <div className={styles.iconItemSm} key={policy.keystonePolicyId}>
              <div className={styles.iconDS}>
                <Icon name={propertyIcon} size="lg" />
              </div>
              <div>
                <Span bold>{lineOne}</Span>
                <br />
                <Span bold>{lineTwo}</Span>
                <div className={styles.subtext}>
                  Policy number: {policy.platformPolicyId}
                </div>
              </div>
            </div>
          );
        })}
      </Stack>
    </MultiStepFormLayout>
  );
};
